/**
 * Swipe Reveal Intro — Farbpanels wischen über den Screen und geben den
 * Marken-Lockup frei.
 *
 * Energiegeladener Opener: zwei Akzent-Panels fegen nacheinander über das
 * Bild; genau wenn sie durch sind, skaliert der Lockup (Logo + Headline +
 * Subline) mittig rein. Pairt sich hervorragend mit den neuen Wipe-/Slide-
 * Transitions zur nächsten Szene.
 *
 * Inputs:
 *   text     – Headline (Default: Markenname)
 *   subtext  – Subline (Default: Marken-Tagline)
 *   extras.eyebrow – kleines Label über der Headline (optional)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, brandName, brandTagline, clipOutroFade,
  combineSceneFragments, escapeHtml, gsapSetLine, makeTl, typeSize, weights,
  withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const swipeRevealIntro: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const headline = (inputs.text ?? brandName).toString();
  const sub = inputs.subtext ?? brandTagline;
  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const headPx = isVertical ? lg * 1.02 : lg * 0.94;
  const subPx = isVertical ? md * 0.56 : md * 0.5;
  const eyePx = sm * 0.62;
  const logoPx = isVertical ? 150 : 132;

  const wrapId = `${clipPrefix}-wrap`;
  const lockupId = `${clipPrefix}-lockup`;
  const logoId = `${clipPrefix}-logo`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const p1Id = `${clipPrefix}-p1`;
  const p2Id = `${clipPrefix}-p2`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:${isVertical ? "100px 80px" : "100px 140px"};">
    <div id="${lockupId}" style="position:relative;display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 24 : 22}px;text-align:center;z-index:1;">
      ${brandLogoImg({ id: logoId, sizePx: logoPx })}
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:7px;text-transform:uppercase;color:${accent};">${escapeHtml(eyebrow)}</div>` : ""}
      <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.02;letter-spacing:-2.4px;color:${themeColors.fg};text-wrap:balance;text-shadow:0 0 60px ${withAlpha(accent, 0.4)};max-width:${isVertical ? 900 : 1200}px;">${escapeHtml(headline)}</div>
      ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subPx}px;letter-spacing:-0.2px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.78)};max-width:${isVertical ? 820 : 1040}px;text-wrap:balance;">${escapeHtml(sub)}</div>` : ""}
    </div>
    <div id="${p2Id}" style="position:absolute;inset:0;z-index:2;background:${withAlpha(themeColors.secondary, 0.96)};"></div>
    <div id="${p1Id}" style="position:absolute;inset:0;z-index:3;background:${accent};"></div>
  </div>`;

  const init = [
    gsapSetLine(`#${p1Id}`, { xPercent: -110 }),
    gsapSetLine(`#${p2Id}`, { xPercent: -110 }),
    gsapSetLine(`#${lockupId}`, { opacity: 0, scale: 0.86 }),
    gsapSetLine(`#${logoId}`, { opacity: 0, y: 24, scale: 0.7 }),
    gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }),
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -6 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 16 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Panels fegen nacheinander durch.
    t.fromTo(`#${p1Id}`, { xPercent: -110 }, { xPercent: 110, duration: 0.62, ease: "power3.inOut" }, 0),
    t.fromTo(`#${p2Id}`, { xPercent: -110 }, { xPercent: 110, duration: 0.66, ease: "power3.inOut" }, 0.12),
    // Lockup taucht hinter dem letzten Panel auf.
    t.to(`#${lockupId}`, { opacity: 1, scale: 1, duration: 0.5, ease: "power2.out" }, 0.5),
    t.to(`#${logoId}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.8)" }, 0.55),
    eyebrow ? t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.7) : "",
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.74),
    sub ? t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0.92) : "",
  ].filter((s): s is string => Boolean(s));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
